//#region assets/vp-variant-sku.ts
/**
* Keeps PDP spec rows (Artikelnummer and variant metafields) in sync with the selected variant.
*
* Specs live in `product-information-tabs`, outside the variant picker's morph target. The picker
* already fetches the full product page for the new option values — this copies the spec lists
* from that document instead of waiting on `resource.sku` alone.
*
* The listener must load with the picker: a `fetchpriority=low` module in the tabs section can
* miss the first `shopify:product:select` if the customer changes variant before it runs.
*/
var SKU_SELECTOR = "[data-vp-variant-sku]";
var SPEC_LIST_SELECTOR = ".product-information-tabs__spec-list";
/**
* @param {string} sku
*/
function applySku(sku) {
	if (!sku) return;
	for (const node of document.querySelectorAll(SKU_SELECTOR)) node.textContent = sku;
}
/**
* @returns {string}
*/
function skuFromPicker() {
	for (const script of document.querySelectorAll("variant-picker script[type=\"application/json\"]")) try {
		const sku = JSON.parse(script.textContent || "").sku;
		if (typeof sku === "string" && sku) return sku;
	} catch {}
	return "";
}
/**
* @param {ParentNode | null | undefined} html
*/
function syncSpecLists(html) {
	if (!html || typeof html.querySelectorAll !== "function") return false;
	const nextLists = html.querySelectorAll(SPEC_LIST_SELECTOR);
	const liveLists = document.querySelectorAll(SPEC_LIST_SELECTOR);
	if (nextLists.length && nextLists.length === liveLists.length) {
		liveLists.forEach((live, index) => {
			const next = nextLists[index];
			if (next) live.innerHTML = next.innerHTML;
		});
		return true;
	}
	const sku = html.querySelector?.(SKU_SELECTOR)?.textContent?.trim();
	if (sku) applySku(sku);
	return Boolean(sku);
}
/**
* @param {Event & { promise?: Promise<{ detail?: { html?: Document, resource?: { sku?: string } | null } }> }} event
*/
function onProductSelect(event) {
	const apply = (result) => {
		const detail = result?.detail ?? {};
		if (syncSpecLists(detail.html)) return;
		applySku(detail.resource?.sku || skuFromPicker());
	};
	const promise = event?.promise;
	if (!promise || typeof promise.then !== "function") {
		apply({});
		return;
	}
	promise.then(apply).catch((error) => {
		apply({});
		if (error?.name !== "AbortError") console.warn("[vp-variant-sku] product select failed:", error);
	});
}
function init() {
	applySku(skuFromPicker());
	document.querySelectorAll("variant-picker").forEach((picker) => {
		if (!(picker instanceof HTMLElement)) return;
		new MutationObserver(() => applySku(skuFromPicker())).observe(picker, {
			childList: true,
			subtree: true,
			characterData: true
		});
	});
}
if (!window.__vpVariantSkuReady) {
	window.__vpVariantSkuReady = true;
	document.addEventListener("shopify:product:select", onProductSelect);
	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
	else init();
}
//#endregion
